import React, { useState } from 'react';
import { omit } from 'lodash';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  InfoCard, Link, StructuredMetadataTable,
} from '@backstage/core-components';
import { ArgusCluster } from '@eng-portal/plugin-argus-common';
import GrainIcon from '@mui/icons-material/Grain';
import {
  AWSAccountResourceEntity,
  EKSClusterResourceEntity,
  getAccountResourceID,
  getClusterResourceID,
  getGrafanaURL,
} from '@eng-portal/czi-extensions-common';

type ClusterCardProps = {
  cluster: ArgusCluster
  titleOverride?: string
  useUtc?: boolean
};

export function ClusterCard({ cluster, titleOverride, useUtc }: ClusterCardProps): JSX.Element {
  const entityRoute = useRouteRef(entityRouteRef);
  const catalogApi = useApi(catalogApiRef);

  const [associatedAwsAccount, setAssociatedAwsAccount] = useState<AWSAccountResourceEntity | undefined>();
  const [associatedCluster, setAssociatedCluster] = useState<EKSClusterResourceEntity | undefined>();

  useAsync(async () => {
    const account = await catalogApi.getEntityByRef({
      kind: 'Resource',
      name: getAccountResourceID(cluster.aws_account_id),
      namespace: 'default',
    });
    const accountEnt = account as AWSAccountResourceEntity;
    setAssociatedAwsAccount(accountEnt);

    const argusClusterEntity = await catalogApi.getEntityByRef({
      kind: 'Resource',
      name: getClusterResourceID(cluster.name, cluster.aws_account_id),
      namespace: 'default',
    });
    const clusterEnt = argusClusterEntity as EKSClusterResourceEntity;
    setAssociatedCluster(clusterEnt);
  }, [cluster, catalogApi]);

  const metadata: Record<string, any> = {
    ...omit(
      cluster,
      'update_time',
      'api_endpoint',
      'certificate_authority_data',
      'manager_bearer_token',
      'raw_kube_config',
      'secret_writer_provider',
      'tiers',
    ),
  };

  if (metadata.aws_account_id && associatedAwsAccount) {
    metadata.aws_account_id = (
      <Link to={entityRoute(parseEntityRef(stringifyEntityRef(associatedAwsAccount)))}>
        {metadata.aws_account_id}
      </Link>
    );
  }

  const cardTitle = titleOverride || "Cluster";

  let clusterLink: JSX.Element | string = cardTitle;
  if (metadata.name && associatedCluster) {
    metadata.name = (
      <Link to={entityRoute(parseEntityRef(stringifyEntityRef(associatedCluster)))}>
        {metadata.name}
      </Link>
    );

    clusterLink = <Link to={entityRoute(parseEntityRef(stringifyEntityRef(associatedCluster)))}>
      {cardTitle}
    </Link>;
  }

  if (metadata.metrics_subdomain && metadata.base_domain) {
    const link = getGrafanaURL(cluster);
    metadata.metrics_url = <Link to={link}>{link}</Link>;
    delete metadata.metrics_subdomain;
  }

  if (useUtc && metadata.create_time) {
    metadata.create_time = new Date(metadata.create_time).toISOString();
  } else if (metadata.create_time) {
    metadata.create_time = new Date(metadata.create_time).toLocaleString();
  }

  const sortedMetadata = Object.entries(metadata)
    .sort(([keyA], [keyB]) => {
      if (keyA === 'id') return -1;
      if (keyB === 'id') return 1;
      return keyA.localeCompare(keyB);
    })
    .reduce((acc: Record<string, any>, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  return (
    <InfoCard title={cardTitle} headerProps={{ title: clusterLink as any, avatar: <GrainIcon /> }}>
      <StructuredMetadataTable metadata={sortedMetadata} />
    </InfoCard>
  );
}
